import { css, html } from 'lit';
import { api, getBackendUrl } from '../../lib/api.js';
import { authState } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

import './landing-page.js';
import '../agreements/agreement-gate.js';
import '../miniatures/miniatures-view.js';
import '../assets/asset-type-view.js';
import '../freelancers/freelancers-view.js';
import '../commissions/commissions-view.js';
import '../pipeline/art-pipeline-view.js';
import '../commissions/available-commissions-view.js';
import '../catalog/asset-catalog-view.js';
import '../staff/staff-settings-view.js';
import '../staff/staff-apps-view.js';
import '../staff/staff-sync-jobs-view.js';
import '../commissions/assignments-view.js';
import '../commissions/staff-assignments-view.js';
import '../freelancers/ledger-view.js';
import '../freelancers/sows-view.js';

const NAV_GROUPS = [
    {
        label: 'Assets',
        icon: 'bi-grid',
        permission: 'assets:admin',
        items: [
            { id: 'images', label: 'Images', path: '/images' },
            { id: 'miniatures', label: 'Miniatures', path: '/miniatures' },
            { id: 'capsules', label: 'Capsules', path: '/capsules' },
            { id: 'hooks', label: 'Hooks', path: '/hooks' },
            { id: 'title-screens', label: 'Title Screens', path: '/title-screens' },
            { id: 'tactical-maps', label: 'Tactical Maps', path: '/tactical-maps' },
            { id: 'tile-brushes', label: 'Tile Brushes', path: '/tile-brushes' },
        ],
    },
    {
        label: 'Freelance',
        icon: 'bi-brush',
        permission: 'commissions:read',
        items: [
            { id: 'available-commissions', label: 'Commissions', path: '/available-commissions' },
            { id: 'assignments', label: 'Assignments', path: '/assignments' },
            { id: 'asset-catalog', label: 'Asset Catalog', path: '/asset-catalog' },
            { id: 'sows', label: 'Statements of Work', path: '/sows' },
            { id: 'ledger', label: 'Ledger', path: '/ledger' },
        ],
    },
    {
        label: 'Staff',
        icon: 'bi-shield-check',
        permission: 'commissions:admin',
        items: [
            { id: 'pipeline', label: 'Pipeline', path: '/pipeline' },
            { id: 'commissions', label: 'Commissions', path: '/commissions' },
            { id: 'staff-assignments', label: 'Assignments', path: '/staff-assignments' },
            { id: 'freelancers', label: 'Freelancers', path: '/freelancers' },
            { id: 'apps', label: 'Apps', path: '/apps' },
            { id: 'sync-jobs', label: 'Sync Jobs', path: '/sync-jobs' },
            { id: 'settings', label: 'Settings', path: '/settings' },
        ],
    },
];

class AssetsApp extends BaseComponent {
    static properties = {
        _loading: { type: Boolean, state: true },
        _authenticated: { type: Boolean, state: true },
        _user: { type: Object, state: true },
        _activeTab: { type: String, state: true },
        _error: { type: String, state: true },
        _showUserMenu: { type: Boolean, state: true },
        _permissionError: { type: String, state: true },
        _agreementsPending: { type: Boolean, state: true },
        // A page can ask for the whole window (issue #204), e.g. the pipeline's
        // wide grid; every other page sits in the xxl container.
        _fullWidth: { type: Boolean, state: true },
        // Below md the nav is a drawer the hamburger opens (issue #233).
        _navOpen: { type: Boolean, state: true },
    };

    static styles = css`
        :host {
            display: block;
            width: 100%;
            min-height: 100vh;
            background-color: #f8f9fa;
        }
        .login-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            gap: 1rem;
        }
        .logo-credit { padding: 1rem; font-size: 0.7rem; line-height: 1.4; color: #999; }
        .logo-credit a { color: inherit; }
        .user-menu { position: relative; }
        .user-menu-toggle {
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.25rem 0.5rem;
            border-radius: 0.375rem;
            border: 1px solid transparent;
            color: #ccc;
        }
        .user-menu-toggle:hover { border-color: #555; }
        .user-dropdown {
            position: absolute;
            right: 0;
            top: 100%;
            margin-top: 0.25rem;
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            box-shadow: 0 0.25rem 0.5rem rgba(0,0,0,0.1);
            min-width: 10rem;
            z-index: 1000;
        }
        .user-dropdown button {
            display: block;
            width: 100%;
            padding: 0.5rem 1rem;
            text-align: left;
            text-decoration: none;
            color: #333;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 0.875rem;
        }
        .user-dropdown button:hover { background-color: #f8f9fa; }

        /* The left nav (issue #233): a sidebar from md up, and below it a
           drawer over the page that the hamburger opens. It's one element, so
           the two can't drift apart. */
        .app-sidebar { width: 220px; flex-shrink: 0; }
        .app-sidebar:focus { outline: none; }
        .nav-toggle {
            background: none;
            border: 1px solid transparent;
            border-radius: 0.375rem;
            color: #ccc;
            font-size: 1.5rem;
            line-height: 1;
            padding: 0.25rem 0.4rem;
        }
        .nav-toggle:hover, .nav-toggle:focus-visible { border-color: #555; color: #fff; }
        .nav-backdrop { display: none; }
        @media (max-width: 767.98px) {
            .app-sidebar {
                position: fixed;
                top: 0;
                bottom: 0;
                left: 0;
                width: min(280px, 85vw);
                z-index: 1045;
                overflow-y: auto;
                transform: translateX(-100%);
                /* Hidden only once it has slid out, and visible as soon as it
                   opens, so its first link can take focus straight away. */
                visibility: hidden;
                transition: transform 0.2s ease, visibility 0s linear 0.2s;
                box-shadow: 0 0 1rem rgba(0, 0, 0, 0.2);
            }
            .app-sidebar.open {
                transform: none;
                visibility: visible;
                transition: transform 0.2s ease, visibility 0s;
            }
            .nav-backdrop {
                display: block;
                position: fixed;
                inset: 0;
                z-index: 1040;
                background: rgba(0, 0, 0, 0.4);
            }
        }
        @media (prefers-reduced-motion: reduce) {
            .app-sidebar { transition: none; }
        }
    `;

    constructor() {
        super();
        this._loading = true;
        this._authenticated = false;
        this._user = null;
        this._activeTab =
            router.currentRoute && router.currentRoute !== 'home'
                ? router.currentRoute
                : 'miniatures';
        this._error = null;
        this._showUserMenu = false;
        this._agreementsPending = false;
        this._fullWidth = false;
        this._navOpen = false;
        this._expandedGroups = new Set(
            NAV_GROUPS.filter((g) => g.items.some((i) => i.id === this._activeTab)).map(
                (g) => g.label,
            ),
        );
    }

    /** The header and the page share one container, so their edges line up. */
    get _layoutContainer() {
        return this._fullWidth ? 'container-fluid' : 'container-xxl';
    }

    get _visibleGroups() {
        return NAV_GROUPS.filter((g) => {
            if (!g.permission) return true;
            return this._user?.permissions?.includes(g.permission);
        });
    }

    /** Opens the nav drawer, holds the page still behind it and focuses its first link. */
    async _openNav() {
        this._navOpen = true;
        document.body.style.overflow = 'hidden';
        await this.updateComplete;
        // Its first link, or the drawer itself when every group is collapsed.
        const nav = /** @type {HTMLElement|null} */ (this.renderRoot.querySelector('#app-nav'));
        (nav?.querySelector('a') ?? nav)?.focus();
    }

    /**
     * Closes the nav drawer. Focus goes back to the hamburger when the drawer was
     * dismissed, but not when a link was followed: the page is changing then.
     */
    _closeNav({ restoreFocus = false } = {}) {
        if (!this._navOpen) return;
        this._navOpen = false;
        document.body.style.overflow = '';
        if (restoreFocus) this.renderRoot.querySelector('.nav-toggle')?.focus();
    }

    _toggleGroup(label) {
        if (this._expandedGroups.has(label)) {
            this._expandedGroups.delete(label);
        } else {
            this._expandedGroups.add(label);
        }
        this.requestUpdate();
    }

    async connectedCallback() {
        super.connectedCallback();
        this._onDocClick = (e) => {
            if (
                this._showUserMenu &&
                !e.composedPath().some((el) => el.classList?.contains('user-menu'))
            ) {
                this._showUserMenu = false;
            }
        };
        document.addEventListener('click', this._onDocClick);
        this._onKeyDown = (e) => {
            if (e.key === 'Escape' && this._navOpen) this._closeNav({ restoreFocus: true });
        };
        document.addEventListener('keydown', this._onKeyDown);
        // Widening past md turns the drawer back into the sidebar, so let the
        // page scroll again.
        this._wideQuery = window.matchMedia('(min-width: 768px)');
        this._onWide = (e) => {
            if (e.matches) this._closeNav();
        };
        this._wideQuery.addEventListener('change', this._onWide);
        // The page asks; a new page starts back in the container (below).
        this.addEventListener('layout-width', (e) => {
            this._fullWidth = Boolean(/** @type {CustomEvent} */ (e).detail?.full);
        });
        this._unsubscribeAuth = authState.subscribe((state) => {
            const wasLoading = this._loading;
            this._loading = state.loading;
            this._authenticated = state.isAuthenticated;
            this._user = state.user;
            // Re-evaluate route after auth completes (replaceState doesn't fire popstate)
            if (wasLoading && !state.loading && state.isAuthenticated) {
                router.handleRoute();
                this._checkAgreements();
            }
        });
        this._unsubscribeRouter = router.subscribe((state) => {
            // Legacy path redirect (wall-brushes -> tile-brushes, issue #57).
            // Rewrite the path prefix so any :id and query string are preserved.
            if (state.meta?.redirect) {
                router.navigate(state.path.replace(/^\/wall-brushes/, '/tile-brushes'));
                return;
            }
            // state.route === null means no addRoute() pattern matched the path
            // — treat it as not-found rather than silently falling through to
            // the default tab (which obscures URL typos + missing route
            // registrations).
            const previousTab = this._activeTab;
            if (state.route === null) {
                this._activeTab = 'not-found';
            } else if (state.route === 'home') {
                this._activeTab = this._defaultTab();
            } else {
                this._activeTab = state.route;
            }
            if (this._activeTab !== previousTab) this._fullWidth = false;
            // Following a link in the drawer closes it.
            this._closeNav();
            // Auto-expand the group containing the active tab
            const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === this._activeTab));
            if (group) this._expandedGroups.add(group.label);
            this._checkPermission();
        });
        this._initAuth();
        router.handleRoute();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._onDocClick);
        document.removeEventListener('keydown', this._onKeyDown);
        this._wideQuery?.removeEventListener('change', this._onWide);
        document.body.style.overflow = '';
        if (this._unsubscribeAuth) this._unsubscribeAuth();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
    }

    async _initAuth() {
        try {
            const response = await fetch(`${getBackendUrl()}/api/config`);
            if (!response.ok) throw new Error('Failed to fetch config');
            const config = await response.json();
            await authState.init({ ...config, appName: 'd3assets' });

            // Auto-redirect to sign in when landing on a deep link while unauthenticated
            if (!authState.isAuthenticated && window.location.pathname !== '/') {
                await authState.login();
            }
        } catch (err) {
            console.error('Auth init failed:', err);
            this._error = err.message;
            this._loading = false;
        }
    }

    _defaultTab() {
        if (this._user?.permissions?.includes('assets:admin')) return 'miniatures';
        if (this._user?.permissions?.includes('commissions:read')) return 'assignments';
        return 'miniatures';
    }

    // Freelancers must accept the current platform documents before they can
    // claim or submit work. Only `assignments:write` holders are gated, and not
    // staff (`commissions:admin`): the agreements are a contractor's terms, and a
    // staff member's own assignment isn't done under them. The API enforces this
    // too; this is the humane front door, not the bar.
    async _checkAgreements() {
        const permissions = this._user?.permissions || [];
        if (
            !permissions.includes('assignments:write') ||
            permissions.includes('commissions:admin')
        ) {
            this._agreementsPending = false;
            return;
        }
        try {
            const data = await api.get('/api/agreements');
            this._agreementsPending = !data.all_accepted;
        } catch {
            // A failed check must not lock a freelancer out of the app; the
            // server-side check still blocks claim and submit.
            this._agreementsPending = false;
        }
    }

    _onAgreementsAccepted() {
        this._agreementsPending = false;
    }

    _checkPermission() {
        if (!this._authenticated || !this._user) return;
        const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === this._activeTab));
        if (group?.permission && !this._user.permissions?.includes(group.permission)) {
            if (!this._permissionError) {
                this._permissionError = `You don't have access to the requested page. You've been redirected to your home page.`;
                router.navigate('/');
            }
        }
    }

    async _handleLogin() {
        await authState.login();
    }
    async _handleLogout() {
        this._showUserMenu = false;
        await authState.logout();
    }

    render() {
        if (this._loading) {
            return html`
                <div class="login-container">
                    <div class="spinner-border text-primary" role="status"></div>
                    <p class="text-muted">Loading...</p>
                </div>
            `;
        }

        if (this._error) {
            return html`
                <div class="login-container">
                    <h4>D3 Assets</h4>
                    <div class="alert alert-danger">${this._error}</div>
                </div>
            `;
        }

        if (!this._authenticated) {
            return html`<landing-page @login="${this._handleLogin}"></landing-page>`;
        }

        return html`
            <!-- The header stays 56px tall (the pipeline's layout counts on it), so the logo takes the brand's padding. -->
            <nav class="navbar navbar-dark bg-dark">
                <div class="${this._layoutContainer} d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center gap-2">
                        <button type="button" class="nav-toggle d-md-none" aria-controls="app-nav"
                            aria-expanded="${this._navOpen ? 'true' : 'false'}"
                            aria-label="${this._navOpen ? 'Close navigation' : 'Open navigation'}"
                            @click="${() => (this._navOpen ? this._closeNav({ restoreFocus: true }) : this._openNav())}">
                            <i class="bi bi-list"></i>
                        </button>
                        <span class="navbar-brand py-0 mb-0 d-flex align-items-center gap-2">
                            <img src="/assets-logo.svg" alt="" style="height: 40px;">
                            <span class="d-none d-sm-inline">Dice Driven Asset System</span>
                            <span class="d-sm-none">D3 Assets</span>
                        </span>
                    </div>
                    <div class="user-menu">
                        <div class="user-menu-toggle" @click="${() => (this._showUserMenu = !this._showUserMenu)}">
                            <i class="bi bi-person-circle" style="font-size: 1.25rem;"></i>
                            <span class="d-none d-md-inline" style="font-size: 0.875rem;">${this._user?.name || this._user?.email || ''}</span>
                            <i class="bi bi-chevron-down" style="font-size: 0.65rem;"></i>
                        </div>
                        ${
                            this._showUserMenu
                                ? html`
                            <div class="user-dropdown">
                                <button @click="${this._handleLogout}">Sign Out</button>
                            </div>
                        `
                                : ''
                        }
                    </div>
                </div>
            </nav>

            <div class="${this._layoutContainer} d-flex" style="min-height: calc(100vh - 56px);">
                ${this._navOpen ? html`<div class="nav-backdrop" @click="${() => this._closeNav({ restoreFocus: true })}"></div>` : ''}
                <nav id="app-nav" aria-label="Main" tabindex="-1" class="app-sidebar bg-white border-end pt-3 d-flex flex-column ${this._navOpen ? 'open' : ''}">
                    ${this._visibleGroups.map((group) => {
                        const expanded = this._expandedGroups.has(group.label);
                        return html`
                            <div class="mb-1">
                                <div class="d-flex align-items-center gap-2 px-3 py-2 text-dark" style="font-size: 0.9rem; font-weight: 500; cursor: pointer;"
                                     @click="${() => this._toggleGroup(group.label)}">
                                    <i class="bi ${group.icon}" style="font-size: 1rem; color: #666;"></i>
                                    <span class="flex-grow-1">${group.label}</span>
                                    <i class="bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'}" style="font-size: 0.7rem; color: #999;"></i>
                                </div>
                                ${
                                    expanded
                                        ? html`
                                    <div class="ms-4">
                                        ${group.items.map(
                                            (item) => html`
                                            <a class="d-block px-3 py-1 text-decoration-none ${this._activeTab === item.id ? 'text-primary fw-medium' : 'text-secondary'}"
                                               style="font-size: 0.85rem;"
                                               href="${item.path}">
                                                ${item.label}
                                            </a>
                                        `,
                                        )}
                                    </div>
                                `
                                        : ''
                                }
                            </div>
                        `;
                    })}
                    <!-- The logo's "Art" badge is CC BY 3.0, so it's credited where the logo shows (#201). -->
                    <div class="logo-credit mt-auto">
                        Logo icon: Art by Muhammad Naufal Subhiansyah from
                        <a href="https://thenounproject.com/browse/icons/term/art/" target="_blank" rel="noopener">Noun Project</a> (CC BY 3.0)
                    </div>
                </nav>

                <main class="flex-grow-1 p-4" style="background: #fff; min-width: 0;">
                    <div style="width: 100%; min-width: 0;">
                        ${
                            this._permissionError
                                ? html`
                            <div class="alert alert-warning alert-dismissible fade show d-flex align-items-center gap-2" role="alert">
                                <i class="bi bi-shield-exclamation"></i>
                                ${this._permissionError}
                                <button type="button" class="btn-close" @click="${() => (this._permissionError = null)}"></button>
                            </div>
                        `
                                : ''
                        }
                        ${
                            this._agreementsPending
                                ? html`<agreement-gate @agreements-accepted="${this._onAgreementsAccepted}"></agreement-gate>`
                                : html`
                        ${this._activeTab === 'miniatures' ? html`<miniatures-view></miniatures-view>` : ''}
                        ${this._activeTab === 'images' ? html`<asset-type-view asset-type="image" base-path="/images"></asset-type-view>` : ''}
                        ${this._activeTab === 'capsules' ? html`<asset-type-view asset-type="capsule" base-path="/capsules"></asset-type-view>` : ''}
                        ${this._activeTab === 'hooks' ? html`<asset-type-view asset-type="hook" base-path="/hooks"></asset-type-view>` : ''}
                        ${this._activeTab === 'title-screens' ? html`<asset-type-view asset-type="title-screen" base-path="/title-screens"></asset-type-view>` : ''}
                        ${this._activeTab === 'tactical-maps' ? html`<asset-type-view asset-type="tactical-map" base-path="/tactical-maps"></asset-type-view>` : ''}
                        ${this._activeTab === 'tile-brushes' ? html`<asset-type-view asset-type="mapmaking-tile-brush" base-path="/tile-brushes"></asset-type-view>` : ''}
                        ${this._activeTab === 'available-commissions' ? html`<available-commissions-view></available-commissions-view>` : ''}
                        ${this._activeTab === 'assignments' ? html`<assignments-view></assignments-view>` : ''}
                        ${this._activeTab === 'asset-catalog' ? html`<asset-catalog-view></asset-catalog-view>` : ''}
                        ${this._activeTab === 'sows' ? html`<sows-view></sows-view>` : ''}
                        ${this._activeTab === 'ledger' ? html`<ledger-view></ledger-view>` : ''}
                        ${this._activeTab === 'pipeline' ? html`<art-pipeline-view></art-pipeline-view>` : ''}
                        ${this._activeTab === 'commissions' ? html`<commissions-view></commissions-view>` : ''}
                        ${this._activeTab === 'staff-assignments' ? html`<staff-assignments-view></staff-assignments-view>` : ''}
                        ${this._activeTab === 'freelancers' ? html`<freelancers-view></freelancers-view>` : ''}
                        ${this._activeTab === 'apps' ? html`<staff-apps-view></staff-apps-view>` : ''}
                        ${this._activeTab === 'sync-jobs' ? html`<staff-sync-jobs-view></staff-sync-jobs-view>` : ''}
                        ${this._activeTab === 'settings' ? html`<staff-settings-view></staff-settings-view>` : ''}
                        ${
                            this._activeTab === 'not-found'
                                ? html`
                            <div class="text-center py-5">
                                <h2 class="text-muted">Page not found</h2>
                                <p class="text-muted"><code>${window.location.pathname}</code> doesn't match any registered route.</p>
                                <a href="/" class="btn btn-outline-primary mt-3">Back to home</a>
                            </div>
                        `
                                : ''
                        }
                        `
                        }
                    </div>
                </main>
            </div>
        `;
    }
}

customElements.define('assets-app', AssetsApp);
